<template>
  <div class="flex-1 flex flex-row w-0 overflow-hidden">
    <div class="flex-1 flex flex-col w-0 overflow-hidden">
      <Spinner v-if="isLoading" class="d-flex align-items-center h-100" />
      <div v-else class="flex flex-col flex-1 overflow-hidden">
        <div class="relative shadow-md">
          <ChatToolbar :conversation="conversation" />
        </div>
        <div class=" flex-1 overflow-y-auto">
          <div class="flex flex-col flex-auto h-full">
            <div
              class="flex flex-col flex-auto flex-shrink-0 rounded-2xl bg-gray-100 h-full"
            >
              <div class="flex flex-col h-full overflow-x-auto ">
                <div class="flex flex-col h-full">
                  <div
                    class="grid grid-cols-12 gap-y-2 overflow-y-auto h-full"
                    v-chat-scroll="{ always: false, scrollonremoved: true }"
                    @scroll="onScroll"
                  >
                    <div class="col-start-1 col-end-13" v-if="isLoadingThreads">
                      <div class="text-center relative my-2">
                        <span
                          class="relative text-xs bg-white py-2 px-4 shadow rounded-full text-gray-800"
                        >
                          Loading...
                        </span>
                      </div>
                    </div>
                    <div
                      class="col-start-1 col-end-13"
                      v-for="(threadGroup, dateLabel) in threads"
                      :key="dateLabel"
                    >
                      <div class="text-center relative">
                        <hr class="absolute w-full top-3" />
                        <span
                          class="relative text-xs bg-white py-2 px-4 shadow rounded-full text-gray-800"
                        >
                          {{ dateLabel }}
                        </span>
                      </div>

                      <ThreadItem
                        v-for="thread in threadGroup"
                        :key="thread.id"
                        :thread="thread"
                        :conversation="conversation"
                        @markAsRead="markThreadAsRead"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <ChatEditor
                :value="body"
                :conversation="conversation"
                @enter="sendMessage"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <InboxRightSidebar
      v-if="conversation.customer"
      :conversation="conversation"
      @sendProductUrl="sendProductUrl"
    />
  </div>
</template>

<script>
import Vue from "vue";

import Spinner from "vue-simple-spinner";

import {
  getDatabase,
  ref,
  remove,
  onChildAdded,
  get,
  off
} from "firebase/database";

import VueChatScroll from "vue-chat-scroll";
import { groupBy } from "lodash-es";
import { startOfDay, format, differenceInHours } from "date-fns";

import InboxRightSidebar from "./InboxRightSidebar";
import ThreadItem from "./ThreadItem";
import ChatEditor from "./ChatEditor";
import ChatToolbar from "./ChatToolbar";

import Utils from "../../Mixins/utils";

Vue.use(VueChatScroll);

export default {
  components: {
    InboxRightSidebar,
    ThreadItem,
    Spinner,
    ChatEditor,
    ChatToolbar
  },
  mixins: [Utils],
  data() {
    return {
      conversation: {},
      conversationId: null,
      isLoading: true,
      threadsRef: null,
      conversationRef: null,
      storeId: null,
      initialDataLoaded: false,
      currentThreadPage: 1,
      isLoadingThreads: false,
      body: "",
      mode: "message"
    };
  },

  computed: {
    threads() {
      const test = groupBy(this.conversation.threads, thread => {
        return format(startOfDay(new Date(thread.createdAt * 1000)), "MMM dd");
      });
      return test;
    },
    allowSendingMessages() {
      const diff = differenceInHours(
        new Date(),
        new Date(this.conversation.lastCustomerReplyTime * 1000)
      );
      return diff < 24;
    }
  },
  watch: {
    "$route.params.conversationId": {
      handler() {
        this.clearActiveListeners();
        Object.assign(this.$data, this.$options.data.apply(this));
        this.conversationId = this.$route.params.conversationId;
        this.init();
      }
    }
  },
  async created() {
    this.init();
  },
  destroyed() {
    this.clearActiveListeners();
  },
  methods: {
    async init() {
      this.clearActiveListeners();
      this.conversationId = this.$route.params.conversationId;
      this.storeId = await this.getStoreId();

      this.loadChat();
    },
    async loadChat() {
      this.isLoading = true;
      this.conversation = await this.$store.dispatch("Inbox/getChat", {
        conversationId: this.conversationId
      });
      this.isLoading = false;
      this.listenThreadChanges(this.storeId, this.conversationId);
      this.listenChatChanges(this.storeId, this.conversationId);
    },
    listenThreadChanges(storeId, conversationId) {
      const database = getDatabase();
      this.threadsRef = ref(
        database,
        `chatThreadsFeed/${storeId}/${conversationId}`
      );

      onChildAdded(this.threadsRef, snapshot => {
        if (this.initialDataLoaded) {
          const newThread = snapshot.val();
          if (this.conversation.id === newThread.conversationId) {
            this.conversation = {
              ...this.conversation,
              threads: [...this.conversation.threads, newThread]
            };
            // this.conversation.threads.push(newThread);
          }
        } else {
          // we are ignoring this child since it is pre-existing data
        }
      });
      get(this.threadsRef).then(() => {
        this.initialDataLoaded = true;
      });
    },
    listenChatChanges(storeId, conversationId) {
      const db = getDatabase();
      this.conversationRef = ref(db, `chatsFeed/${storeId}`);
      onChildAdded(this.conversationRef, snapshot => {
        if (snapshot.key === conversationId) {
          const tempThread = this.conversation.threads;
          this.conversation = { ...snapshot.val(), threads: tempThread };
          if (this.conversation.isDeleted) {
            this.$router.push({ name: "Chats" });
          }
        }
      });
    },
    async loadMoreThreadsIfPossible() {
      const nextPage = this.currentThreadPage + 1;
      if (
        this.isLoadingThreads === false &&
        nextPage <= this.conversation.totalThreadPages
      ) {
        this.isLoadingThreads = true;

        const threads = await this.$store.dispatch("Inbox/getThreads", {
          conversationId: this.conversationId,
          page: nextPage
        });
        this.conversation.threads = [...threads, ...this.conversation.threads];
        this.isLoadingThreads = false;
        this.currentThreadPage = nextPage;
      }
    },
    onScroll(event) {
      const innerHeight = event.target.scrollHeight;
      const offsetHeight = event.target.offsetHeight;
      const scrollTop = event.target.scrollTop;

      console.log("innerHeight", innerHeight);
      console.log("offsetHeight", offsetHeight);
      console.log("scrollTop", scrollTop);
      // const isBottomOfContainer = offsetHeight + scrollTop >= innerHeight;
      if (scrollTop === 0) {
        this.loadMoreThreadsIfPossible();
      }
    },
    async sendMessage(body) {
      //   if (this.showAcceptChat) {
      //     await this.$store.dispatch("Chat/acceptChat", {
      //       chatId: this.chatId,
      //       mailboxId: this.mailboxId
      //     });
      //   }
      await this.$store.dispatch("Inbox/sendChat", {
        conversationId: this.conversationId,
        requestParams: {
          type: this.mode,
          body
        }
      });
      this.body = "";
    },
    clearActiveListeners() {
      if (this.threadsRef) {
        off(this.threadsRef);
      }
      // if (this.conversationRef && this.conversationRef.off) {
      //   this.conversationRef.off();
      // }
      if (this.typingIndicatorRef) {
        off(this.typingIndicatorRef);
      }
    },
    markThreadAsRead(threadId) {
      const db = getDatabase();
      return remove(
        ref(
          db,
          `chatCounts/${this.storeId}/${this.conversation.id}/${threadId}`
        )
      );
    },
    sendProductUrl(url) {
      if (this.allowSendingMessages) {
        this.sendMessage(url);
      } else {
        alert(`You can't send message to this customer - Time limit exceeded`);
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>
